import {
    PublicKey,
    Transaction,
    SystemProgram,
    LAMPORTS_PER_SOL,
    Connection,
    clusterApiUrl,
} from '@solana/web3.js';
import {
    createTransferInstruction,
    getAssociatedTokenAddress,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
    createAssociatedTokenAccountInstruction,
} from '@solana/spl-token';
import { detectConnectedWallet } from '../utils/walletDetection';

// Use the Solana mainnet RPC URL from environment variable
const RPC_URL = import.meta.env.VITE_SOLANA_RPC_URL;

// Create connection with preflight commitment level
const connection = new Connection(RPC_URL, 'confirmed');

export interface TransferResult {
    success: boolean;
    signature?: string;
    error?: string;
}

/**
 * Send SOL to another wallet
 * @param recipient Recipient address
 * @param amount Amount in SOL (not lamports)
 */
export const sendSol = async (recipient: string, amount: number): Promise<TransferResult> => {
    try {
        // Detect which wallet is connected
        const wallet = detectConnectedWallet();
        if (!wallet) {
            return { success: false, error: 'No wallet connected' };
        }

        // Get wallet provider (Phantom or Solflare)
        const provider = wallet.provider === 'phantom' ? window.solana : window.solflare;
        if (!provider || !provider.publicKey) {
            return { success: false, error: 'Wallet not properly connected' };
        }

        // Convert wallet's publicKey to a proper Solana PublicKey object
        const walletPublicKey = new PublicKey(provider.publicKey.toString());

        // Convert amount to lamports (1 SOL = 1,000,000,000 lamports)
        const lamports = Math.floor(amount * LAMPORTS_PER_SOL);

        // Create transaction
        const transaction = new Transaction().add(
            SystemProgram.transfer({
                fromPubkey: walletPublicKey,
                toPubkey: new PublicKey(recipient),
                lamports
            })
        );

        // Set recent blockhash and fee payer
        const { blockhash } = await connection.getLatestBlockhash('confirmed');
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = walletPublicKey;

        // Request signature from the wallet
        let signedTransaction;
        try {
            signedTransaction = await provider.signTransaction(transaction);
        } catch (error) {
            console.error("Error signing the transaction:", error);
            return { success: false, error: 'Transaction signing rejected by user' };
        }

        // Send the transaction
        const signature = await connection.sendRawTransaction(signedTransaction.serialize());

        // Wait for confirmation
        await connection.confirmTransaction(signature, 'confirmed');

        // Log success
        console.log('SOL transfer successful', signature);

        // Dispatch custom events to trigger balance updates
        // First, get the updated balances
        try {
            console.log(`[sendSol] Fetching updated balances after transfer`);

            // Get the sender's new balance
            const senderBalance = await connection.getBalance(walletPublicKey);
            console.log(`[sendSol] Sender balance: ${senderBalance / LAMPORTS_PER_SOL} SOL`);

            // Get the recipient's new balance (optional)
            try {
                const recipientBalance = await connection.getBalance(new PublicKey(recipient));
                console.log(`[sendSol] Recipient balance: ${recipientBalance / LAMPORTS_PER_SOL} SOL`);

                // Dispatch event for recipient (only if it's a trading wallet we're managing)
                window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                    detail: {
                        address: recipient,
                        balance: recipientBalance
                    }
                }));
            } catch (err) {
                console.log(`[sendSol] Could not fetch recipient balance: ${err}`);
            }

            // Always dispatch event for sender
            window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                detail: {
                    address: walletPublicKey.toString(),
                    balance: senderBalance
                }
            }));

        } catch (err) {
            console.error("[sendSol] Error dispatching balance updates:", err);
        }

        return { success: true, signature };
    } catch (error) {
        console.error('Error in SOL transfer:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error in SOL transfer'
        };
    }
};

/**
 * Send SPL tokens to another wallet
 * @param recipient Recipient address
 * @param amount Amount of tokens to send (in token units, not raw)
 * @param mintAddress The token's mint address
 * @param decimals The token's decimal places
 */
export const sendToken = async (
    recipient: string,
    amount: number,
    mintAddress: string,
    decimals: number
): Promise<TransferResult> => {
    try {
        // Special case for SOL which is native
        if (mintAddress === 'native') {
            return sendSol(recipient, amount);
        }

        // Detect which wallet is connected
        const wallet = detectConnectedWallet();
        if (!wallet) {
            return { success: false, error: 'No wallet connected' };
        }

        // Get wallet provider (Phantom or Solflare)
        const provider = wallet.provider === 'phantom' ? window.solana : window.solflare;
        if (!provider || !provider.publicKey) {
            return { success: false, error: 'Wallet not properly connected' };
        }

        // Convert wallet's publicKey to a proper Solana PublicKey object
        const walletPublicKey = new PublicKey(provider.publicKey.toString());

        // Convert recipient and mint to PublicKey
        const recipientPubkey = new PublicKey(recipient);
        const mint = new PublicKey(mintAddress);

        // Calculate amount with decimals
        const adjustedAmount = Math.floor(amount * (10 ** decimals));

        // Get associated token accounts
        const senderATA = await getAssociatedTokenAddress(
            mint,
            walletPublicKey,
            false,
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
        );

        // Get or create recipient's associated token account
        const recipientATA = await getAssociatedTokenAddress(
            mint,
            recipientPubkey,
            false,
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
        );

        // Create transaction
        const transaction = new Transaction();

        // If token account doesn't exist for recipient, create it
        const connection = new Connection(clusterApiUrl('mainnet-beta'));

        try {
            await connection.getAccountInfo(recipientATA);
        } catch (error) {
            // Add instruction to create associated token account
            transaction.add(
                createAssociatedTokenAccountInstruction(
                    walletPublicKey,
                    recipientATA,
                    recipientPubkey,
                    mint,
                    TOKEN_PROGRAM_ID,
                    ASSOCIATED_TOKEN_PROGRAM_ID
                )
            );
        }

        // Add transfer instruction
        transaction.add(
            createTransferInstruction(
                senderATA,
                recipientATA,
                walletPublicKey,
                adjustedAmount,
                [],
                TOKEN_PROGRAM_ID
            )
        );

        // Set recent blockhash and fee payer
        const { blockhash } = await connection.getLatestBlockhash('confirmed');
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = walletPublicKey;

        // Request signature from the wallet
        let signedTransaction;
        try {
            signedTransaction = await provider.signTransaction(transaction);
        } catch (error) {
            console.error("Error signing the transaction:", error);
            return { success: false, error: 'Transaction signing rejected by user' };
        }

        // Send the transaction
        const signature = await connection.sendRawTransaction(signedTransaction.serialize());

        // Wait for confirmation
        await connection.confirmTransaction(signature, 'confirmed');

        // Log success
        console.log('Token transfer successful', signature);

        // Dispatch custom events to trigger balance updates
        try {
            console.log(`[sendToken] Fetching updated token balances after transfer`);

            // Get the sender's new token balance
            try {
                const senderTokenInfo = await connection.getTokenAccountBalance(senderATA);
                const senderBalance = senderTokenInfo?.value?.uiAmount?.toString() || '0';
                console.log(`[sendToken] Sender token balance: ${senderBalance}`);

                // Dispatch token balance update for sender
                window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                    detail: {
                        address: walletPublicKey.toString(),
                        mint: mintAddress,
                        balance: senderBalance
                    }
                }));
            } catch (err) {
                console.error(`[sendToken] Could not fetch sender token balance: ${err}`);
            }

            // Get the recipient's new token balance (optional)
            try {
                const recipientTokenInfo = await connection.getTokenAccountBalance(recipientATA);
                const recipientBalance = recipientTokenInfo?.value?.uiAmount?.toString() || '0';
                console.log(`[sendToken] Recipient token balance: ${recipientBalance}`);

                // Dispatch token balance update for recipient (only if it's a trading wallet we're managing)
                window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                    detail: {
                        address: recipient,
                        mint: mintAddress,
                        balance: recipientBalance
                    }
                }));
            } catch (err) {
                console.log(`[sendToken] Could not fetch recipient token balance: ${err}`);
            }

        } catch (err) {
            console.error("[sendToken] Error dispatching token balance updates:", err);
        }

        return { success: true, signature };
    } catch (error) {
        console.error('Error in token transfer:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error in token transfer'
        };
    }
}; 